import type { ModalVariantProps } from "@kirvanobr/theme";

import { ReactRef, useDOMRef } from "@kirvanobr/react-utils";
import { clsx } from "@kirvanobr/shared-utils";
import { HTMLUIProps, mapPropsVariants, PropGetter } from "@kirvanobr/system";
import { mergeProps, mergeRefs } from "@react-aria/utils";
import { ReactNode, useCallback } from "react";
import { modal } from "@kirvanobr/theme";

import { useModalContext } from "./modal-context";

interface Props extends HTMLUIProps<"section"> {
  /**
   * Ref to the DOM node.
   */
  ref?: ReactRef<HTMLElement | null>;
  /**
   * The props to modify the framer motion animation. Use the `variants` API to create your own animation.
   */
  leftContent?: ReactNode;
}

export type UseModalFooterProps = Props & ModalVariantProps;

export function useModalFooter(originalProps: UseModalFooterProps) {
  const { slots, classNames } = useModalContext();
  const [props, variantProps] = mapPropsVariants(originalProps, modal.variantKeys);

  const { ref, as, className, children, leftContent, ...otherProps } = props;

  const domRef = useDOMRef(ref);
  const Component = as || "footer";

  const baseStyles = clsx(slots?.footer, className);

  const getFooterProps = useCallback<PropGetter>(
    (props = {}) => ({
      className: slots.footer({ class: clsx(baseStyles) }),
      ref: mergeRefs(domRef, props.ref),
      ...mergeProps(otherProps, props),
    }),
    [slots, className],
  );

  const getFooterButtonsProps = useCallback<PropGetter>(
    (props = {}) => ({
      className: slots.footerButtons({ class: clsx(classNames?.footerButtons) }),
      ...props,
    }),
    [slots, className],
  );

  const getFooterLeftContentProps = useCallback<PropGetter>(
    (props = {}) => ({
      className: slots.footerLeftContent({ class: clsx(classNames?.footerLeftContent) }),
      ...props,
    }),
    [slots, className],
  );

  return {
    Component,
    children,
    leftContent,
    getFooterProps,
    getFooterButtonsProps,
    getFooterLeftContentProps,
  };
}

export type UseModalFooterReturn = ReturnType<typeof useModalFooter>;
